import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

/*
========================================================
TENANT COCKPIT PAGE
--------------------------------------------------------
Purpose:
- Shows one tenant in the platform admin area
- Allows plan/status updates
- Allows tenant admin promotion/removal
- Shows business profile, users, and notes
- Adds Platform Management dropdown for admin navigation
- Notes are displayed newest first
========================================================
*/

function fmtDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function titleCase(value) {
  if (!value) return "—";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

function fullAddress(profile) {
  if (!profile) return "—";
  const parts = [
    profile.address_line1,
    profile.address_line2,
    [profile.city, profile.state, profile.postal_code].filter(Boolean).join(", "),
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

export default function PlatformTenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savingTenant, setSavingTenant] = useState(false);
  const [actingUserId, setActingUserId] = useState(null);
  const [showPlatformMenu, setShowPlatformMenu] = useState(false);

  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [billingMode, setBillingMode] = useState("");

  async function loadTenant() {
    setErr("");

    try {
      const res = await fetch(`/api/platform/tenants/${tenantId}`, {
        credentials: "include",
        headers: {
          "x-tenant": "dfasio",
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed with ${res.status}`);
      }

      const json = await res.json();
      setData(json);

      const tenant = json.tenant || {};
      setPlan(tenant.subscription_plan || "");
      setStatus(tenant.subscription_status || "");
      setBillingMode(tenant.billing_mode || "");
    } catch (e) {
      setErr(e.message || "Failed to load tenant");
    }
  }

  useEffect(() => {
    loadTenant();
  }, [tenantId]);

  async function saveTenantSettings(e) {
    e.preventDefault();
    setSavingTenant(true);
    setErr("");

    try {
      const res = await fetch(`/api/platform/tenants/${tenantId}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-tenant": "dfasio",
        },
        body: JSON.stringify({
          subscription_plan: plan,
          subscription_status: status,
          billing_mode: billingMode,
        }),
      });

      if (!res.ok) {
        let message = "Failed to save tenant settings";

        try {
          const data = await res.json();
          message = data.detail || message;
        } catch {
          const text = await res.text().catch(() => "");
          if (text) message = text;
        }

        throw new Error(message);
      }

      await loadTenant();
    } catch (e) {
      setErr(e.message || "Failed to save tenant settings");
    } finally {
      setSavingTenant(false);
    }
  }

  async function toggleTenantAdmin(userId, makeAdmin) {
    setActingUserId(userId);
    setErr("");

    try {
      const res = await fetch(
        `/api/platform/tenants/${tenantId}/users/${userId}/admin`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-tenant": "dfasio",
          },
          body: JSON.stringify({
            is_admin: makeAdmin,
          }),
        }
      );

      if (!res.ok) {
        let message = "Failed to update user";

        try {
          const data = await res.json();
          message = data.detail || message;
        } catch {
          const text = await res.text().catch(() => "");
          if (text) message = text;
        }

        throw new Error(message);
      }

      await loadTenant();
    } catch (e) {
      setErr(e.message || "Failed to update user admin status");
    } finally {
      setActingUserId(null);
    }
  }

  async function togglePlatformAdmin(userId, makePlatformAdmin) {
    setActingUserId(userId);
    setErr("");

    try {
      const res = await fetch(
        `/api/platform/tenants/${tenantId}/users/${userId}/platform-admin`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-tenant": "dfasio",
          },
          body: JSON.stringify({
            is_platform_admin: makePlatformAdmin,
          }),
        }
      );

      if (!res.ok) {
        let message = "Failed to update platform admin status";

        try {
          const data = await res.json();
          message = data.detail || message;
        } catch {
          const text = await res.text().catch(() => "");
          if (text) message = text;
        }

        throw new Error(message);
      }

      await loadTenant();
    } catch (e) {
      setErr(e.message || "Failed to update platform admin status");
    } finally {
      setActingUserId(null);
    }
  }

  async function saveNote(e) {
    e.preventDefault();

    const content = noteText.trim();
    if (!content) return;

    setSavingNote(true);
    setErr("");

    try {
      const res = await fetch(`/api/platform/tenants/${tenantId}/notes`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-tenant": "dfasio",
        },
        body: JSON.stringify({ content }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed with ${res.status}`);
      }

      setNoteText("");
      await loadTenant();
    } catch (e) {
      setErr(e.message || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  }


  const menuBtnStyle = {
    padding: "10px 16px",
    background: "#e0b84f",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    color: "#0b1b2b",
    fontWeight: 700,
  };

  const menuPanelStyle = {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 8,
    minWidth: 240,
    background: "#10263a",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 10,
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    overflow: "hidden",
    zIndex: 50,
  };

  const menuItemStyle = {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "12px 14px",
    background: "transparent",
    color: "#ffffff",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
  };

  if (err && !data) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 18 }}>
          <Link to="/platform/tenants" style={{ color: "#00c2ff" }}>
            ← Back to tenants
          </Link>
        </div>

        <h1 style={{ marginTop: 0 }}>Tenant Cockpit</h1>
        <div style={{ color: "#ff8a8a", marginTop: 12 }}>{err}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ marginTop: 0 }}>Tenant Cockpit</h1>
        <div style={{ color: "#9fb0c3", marginTop: 12 }}>Loading...</div>
      </div>
    );
  }

  const tenant = data.tenant || {};
  const businessProfile = data.business_profile || {};
  const users = data.users || [];
  const notes = data.notes || [];

  const activeUsers = users.filter((u) => u.is_active).length;
  const adminUsers = users.filter((u) => u.is_admin).length;
  const platformAdmins = users.filter((u) => u.is_platform_admin).length;


  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 18 }}>
        <Link to="/platform/tenants" style={{ color: "#00c2ff" }}>
          ← Back to tenants
        </Link>
      </div>

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: "0 0 8px" }}>Tenant Cockpit — {tenant.name}</h1>
        <div style={{ color: "#9fb0c3" }}>
          {tenant.subdomain || "—"} · Tenant ID {tenant.id || "—"}
        </div>
      </div>

      {err && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(255,0,0,0.08)",
            border: "1px solid rgba(255,0,0,0.18)",
            color: "#ffb3b3",
            fontWeight: 500,
          }}
        >
          {err === "Cannot remove the last workspace admin from tenant"
            ? "You cannot remove the last remaining workspace admin from this tenant."
            : err}
        </div>
      )}

      {/* Top summary strip */}
      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>{tenant.name}</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 14,
          }}
        >
          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Plan</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>
              {titleCase(tenant.subscription_plan)}
            </div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Status</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>
              {titleCase(tenant.subscription_status)}
            </div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Users</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{users.length}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Tenant Type</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>
              {titleCase(tenant.tenant_type || "standard")}
            </div>
          </div>
        </div>
      </div>

      {/* Tenant Health Strip */}
      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 6 }}>Tenant Health</h2>
        <div style={{ color: "#9fb0c3", fontSize: 13, marginBottom: 14 }}>
          Health signals show billing, workspace access, payment infrastructure, AI, and communication readiness.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 999,
              background:
                tenant.subscription_status === "active"
                  ? "rgba(34,197,94,0.14)"
                  : tenant.subscription_status === "past_due"
                  ? "rgba(234,179,8,0.14)"
                  : "rgba(239,68,68,0.14)",
              border:
                tenant.subscription_status === "active"
                  ? "1px solid rgba(34,197,94,0.35)"
                  : tenant.subscription_status === "past_due"
                  ? "1px solid rgba(234,179,8,0.35)"
                  : "1px solid rgba(239,68,68,0.35)",
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background:
                  tenant.subscription_status === "active"
                    ? "#22c55e"
                    : tenant.subscription_status === "past_due"
                    ? "#eab308"
                    : "#ef4444",
              }}
            />
            {tenant.subscription_status === "active"
              ? "Billing Active"
              : tenant.subscription_status === "past_due"
              ? "Billing Past Due"
              : tenant.subscription_status === "inactive"
              ? "Billing Inactive"
              : "Billing Canceled"}
          </span>

          <span
            title="Stripe Connect is used later so tenants can receive customer payments and payouts through JobTelesis."
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 999,
              background: tenant.stripe_connect_account_id
                ? "rgba(34,197,94,0.14)"
                : "rgba(239,68,68,0.14)",
              border: tenant.stripe_connect_account_id
                ? "1px solid rgba(34,197,94,0.35)"
                : "1px solid rgba(239,68,68,0.35)",
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: tenant.stripe_connect_account_id
                  ? "#22c55e"
                  : "#ef4444",
              }}
            />
            {tenant.stripe_connect_account_id
              ? "Stripe Connect Active"
              : "Stripe Connect Not Connected"}
          </span>

          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 999,
              background: tenant.ai_enabled
                ? "rgba(34,197,94,0.14)"
                : "rgba(239,68,68,0.14)",
              border: tenant.ai_enabled
                ? "1px solid rgba(34,197,94,0.35)"
                : "1px solid rgba(239,68,68,0.35)",
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: tenant.ai_enabled
                  ? "#22c55e"
                  : "#ef4444",
              }}
            />
            {tenant.ai_enabled
              ? "AI Enabled"
              : "AI Disabled"}
          </span>

          <span style={{ padding: "8px 12px", borderRadius: 999, background: businessProfile.email ? "rgba(34,197,94,0.14)" : "rgba(239,68,68,0.14)", border: businessProfile.email ? "1px solid rgba(34,197,94,0.35)" : "1px solid rgba(239,68,68,0.35)", fontWeight: 700 }}>
            {businessProfile.email ? "Email Configured" : "Missing Email"}
          </span>

          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 999,
              background:
                tenant.subscription_status === "active"
                  ? "rgba(34,197,94,0.14)"
                  : tenant.subscription_status === "past_due"
                  ? "rgba(234,179,8,0.14)"
                  : "rgba(239,68,68,0.14)",
              border:
                tenant.subscription_status === "active"
                  ? "1px solid rgba(34,197,94,0.35)"
                  : tenant.subscription_status === "past_due"
                  ? "1px solid rgba(234,179,8,0.35)"
                  : "1px solid rgba(239,68,68,0.35)",
              fontWeight: 700,
            }}
          >
            {tenant.subscription_status === "active"
              ? "Workspace Healthy"
              : tenant.subscription_status === "past_due"
              ? "Payment Attention Needed"
              : "Workspace Limited"}
          </span>

          <span
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              background:
                tenant.tenant_type === "beta"
                  ? "rgba(59,130,246,0.18)"
                  : "rgba(34,197,94,0.14)",
              border:
                tenant.tenant_type === "beta"
                  ? "1px solid rgba(96,165,250,0.35)"
                  : "1px solid rgba(34,197,94,0.35)",
              fontWeight: 700,
            }}
          >
            Tenant Type: {titleCase(tenant.tenant_type || "standard")}
          </span>

          <span style={{ padding: "8px 12px", borderRadius: 999, background: "rgba(34,197,94,0.14)", border: "1px solid rgba(34,197,94,0.35)", fontWeight: 700 }}>
            {titleCase(tenant.subscription_plan)} Plan
          </span>
        </div>
      </div>

      {/* Business Profile */}
      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
            position: "relative",
          }}
        >
          <h2 style={{ margin: 0 }}>Business Profile</h2>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              style={menuBtnStyle}
              onClick={() => setShowPlatformMenu((v) => !v)}
            >
              Platform Management ▼
            </button>

            {showPlatformMenu && (
              <div style={menuPanelStyle}>
                <button
                  type="button"
                  style={menuItemStyle}
                  onClick={() => {
                    setShowPlatformMenu(false);
                    navigate("/platform");
                  }}
                >
                  Open Platform Management
                </button>

                <button
                  type="button"
                  style={menuItemStyle}
                  onClick={() => {
                    setShowPlatformMenu(false);
                    navigate("/platform/tenants");
                  }}
                >
                  Back to Tenant List
                </button>

                <button
                  type="button"
                  style={menuItemStyle}
                  onClick={() => {
                    setShowPlatformMenu(false);
                    document
                      .getElementById("workspace-section")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  Go to Workspace Details
                </button>

                <button
                  type="button"
                  style={menuItemStyle}
                  onClick={() => {
                    setShowPlatformMenu(false);
                    document
                      .getElementById("users-section")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  Go to Users
                </button>

                <button
                  type="button"
                  style={menuItemStyle}
                  onClick={() => {
                    setShowPlatformMenu(false);
                    document
                      .getElementById("notes-section")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  Go to Notes
                </button>
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "220px 1fr",
            rowGap: 10,
          }}
        >
          <div style={{ color: "#9fb0c3" }}>Legal Name</div>
          <div>{businessProfile.legal_name || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Display Name</div>
          <div>{businessProfile.display_name || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Owner Name</div>
          <div>{businessProfile.owner_name || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Email</div>
          <div>{businessProfile.email || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Phone</div>
          <div>{businessProfile.phone || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Website</div>
          <div>{businessProfile.website || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Address</div>
          <div>{fullAddress(businessProfile)}</div>

          <div style={{ color: "#9fb0c3" }}>Reply-To Email</div>
          <div>{businessProfile.reply_to_email || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Support Email</div>
          <div>{businessProfile.support_email || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Profile Timezone</div>
          <div>{businessProfile.timezone || "—"}</div>
        </div>
      </div>

      {/* Workspace Details */}
      <div
        id="workspace-section"
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Workspace Details</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "220px 1fr",
            rowGap: 10,
            marginBottom: 20,
          }}
        >
          <div style={{ color: "#9fb0c3" }}>Tenant ID</div>
          <div>{tenant.id}</div>

          <div style={{ color: "#9fb0c3" }}>Name</div>
          <div>{tenant.name}</div>

          <div style={{ color: "#9fb0c3" }}>Subdomain</div>
          <div>{tenant.subdomain}</div>

          <div style={{ color: "#9fb0c3" }}>Tenant Type</div>
          <div>{titleCase(tenant.tenant_type || "standard")}</div>

          <div style={{ color: "#9fb0c3" }}>Created</div>
          <div>{fmtDate(tenant.created_at)}</div>

          <div style={{ color: "#9fb0c3" }}>Timezone</div>
          <div>{tenant.timezone || "—"}</div>

          <div style={{ color: "#9fb0c3" }}>Judgments Enabled</div>
          <div>{tenant.judgments_enabled ? "Yes" : "No"}</div>
        </div>

        <form onSubmit={saveTenantSettings}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "220px 1fr",
              rowGap: 12,
              alignItems: "center",
            }}
          >
            <div style={{ color: "#9fb0c3" }}>Subscription Plan</div>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              style={{
                borderRadius: 8,
                border: "1px solid #27496b",
                background: "#10263a",
                color: "#ffffff",
                padding: "10px 12px",
                maxWidth: 320,
              }}
            >
              <option value="beta">Beta</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>

            <div style={{ color: "#9fb0c3" }}>Billing Mode</div>
            <select
              value={billingMode}
              onChange={(e) => setBillingMode(e.target.value)}
              style={{
                borderRadius: 8,
                border: "1px solid #27496b",
                background: "#10263a",
                color: "#ffffff",
                padding: "10px 12px",
                maxWidth: 320,
              }}
            >
              <option value="free">Free</option>
              <option value="beta">Beta</option>
              <option value="trial">Trial</option>
              <option value="paid">Paid</option>
            </select>

            <div style={{ color: "#9fb0c3" }}>Subscription Status</div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                borderRadius: 8,
                border: "1px solid #27496b",
                background: "#10263a",
                color: "#ffffff",
                padding: "10px 12px",
                maxWidth: 320,
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="past_due">Past Due</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={savingTenant}
            style={{
              marginTop: 16,
              padding: "10px 16px",
              background: "#00c2ff",
              border: "none",
              borderRadius: 8,
              cursor: savingTenant ? "not-allowed" : "pointer",
              color: "#062033",
              fontWeight: 700,
              opacity: savingTenant ? 0.75 : 1,
            }}
          >
            {savingTenant ? "Saving..." : "Save Tenant Changes"}
          </button>
        </form>
      </div>

      {/* Billing & Subscription History */}
      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Billing & Subscription History</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 24,
            marginTop: 20,
          }}
        >
          <div
            style={{
              background: "#10263a",
              borderRadius: 10,
              padding: 18,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Billing</h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "220px 1fr",
                rowGap: 10,
              }}
            >
              <div style={{ color: "#9fb0c3" }}>Subscription Plan</div>
              <div>{titleCase(tenant.subscription_plan)}</div>

              <div style={{ color: "#9fb0c3" }}>Billing Mode</div>
              <div>{titleCase(tenant.billing_mode)}</div>

              <div style={{ color: "#9fb0c3" }}>Subscription Status</div>
              <div>{titleCase(tenant.subscription_status)}</div>

              <div style={{ color: "#9fb0c3" }}>Trial Ends</div>
              <div>{fmtDate(tenant.trial_ends_at)}</div>

              <div style={{ color: "#9fb0c3" }}>Current Billing Period End</div>
              <div>{fmtDate(tenant.stripe_current_period_end)}</div>

              <div style={{ color: "#9fb0c3" }}>Cancel At Period End</div>
              <div>{tenant.stripe_cancel_at_period_end ? "Yes" : "No"}</div>

              <div style={{ color: "#9fb0c3" }}>Stripe Customer ID</div>
              <div>{tenant.stripe_customer_id || "—"}</div>

              <div style={{ color: "#9fb0c3" }}>Stripe Subscription ID</div>
              <div>{tenant.stripe_subscription_id || "—"}</div>
            </div>
          </div>

          <div
            style={{
              background: "#10263a",
              borderRadius: 10,
              padding: 18,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Access</h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "220px 1fr",
                rowGap: 10,
              }}
            >
              <div style={{ color: "#9fb0c3" }}>Access Status</div>
              <div>{tenant.access_status || "—"}</div>

              <div style={{ color: "#9fb0c3" }}>Suspended</div>
              <div>{tenant.is_suspended ? "Yes" : "No"}</div>

              <div style={{ color: "#9fb0c3" }}>Admin Override</div>
              <div>{tenant.admin_override ? "Yes" : "No"}</div>

              <div style={{ color: "#9fb0c3" }}>Entitlement Source</div>
              <div>
                {tenant.tenant_type === "beta" || tenant.billing_mode === "beta"
                  ? "Beta Program Access"
                  : tenant.billing_mode === "free"
                  ? "Platform Free Access"
                  : tenant.billing_mode === "trial"
                  ? "Trial Access"
                  : tenant.billing_mode === "paid"
                  ? "Paid Subscription"
                  : "Unknown"}
              </div>

              <div style={{ color: "#9fb0c3" }}>Last Entitlement Sweep</div>
              <div>System Managed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Operational Onboarding */}
      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 6 }}>Operational Onboarding</h2>

        <div style={{ color: "#9fb0c3", fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
          Tracks tenant activation using real workflow activity. Jay Gomez and Jan Gomez can use this
          section to understand what the tenant has completed and what should happen next.
        </div>

        <div style={{ background: "#10263a", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#9fb0c3", fontSize: 13 }}>Smart Onboarding Progress</div>
              <div style={{ fontSize: 28, fontWeight: 800 }}>
                {tenant.calculated_onboarding_progress ?? tenant.onboarding_progress ?? 0}%
              </div>
            </div>

            <div>
              <div style={{ color: "#9fb0c3", fontSize: 13 }}>Signup Stage</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>
                {titleCase((tenant.signup_stage || "unknown").replaceAll("_", " "))}
              </div>
            </div>

            <div>
              <div style={{ color: "#9fb0c3", fontSize: 13 }}>Next Best Action</div>
              <div style={{ fontSize: 14, fontWeight: 700, maxWidth: 360, lineHeight: 1.5 }}>
                {tenant.suggested_next_action || "Guide this tenant through the next operational milestone."}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              height: 10,
              borderRadius: 999,
              background: "#071827",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.max(0, Math.min(100, tenant.calculated_onboarding_progress ?? tenant.onboarding_progress ?? 0))}%`,
                background: "#e0b84f",
                borderRadius: 999,
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 14,
            marginBottom: 16,
          }}
        >
          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Clients</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{tenant.onboarding_counts?.clients ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Jobs</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{tenant.onboarding_counts?.jobs ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Estimates</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{tenant.onboarding_counts?.estimates ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Invoices</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{tenant.onboarding_counts?.invoices ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Payments / Receipts</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{tenant.onboarding_counts?.payments_receipts ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Verification</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>
              {tenant.verification_required
                ? tenant.verification_completed_at
                  ? "Completed"
                  : "Required"
                : "Not Required"}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
          {(tenant.onboarding_milestones || []).map((m) => (
            <div
              key={m.key}
              style={{
                display: "grid",
                gridTemplateColumns: "170px 90px 1fr",
                gap: 12,
                alignItems: "center",
                background: "#10263a",
                borderRadius: 10,
                padding: 12,
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <div style={{ fontWeight: 800 }}>{m.label}</div>
              <div
                style={{
                  color: m.complete ? "#86efac" : "#facc15",
                  fontWeight: 800,
                }}
              >
                {m.complete ? "Complete" : "Pending"}
              </div>
              <div style={{ color: "#9fb0c3", fontSize: 13, lineHeight: 1.5 }}>
                {m.meaning}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            background: "#10263a",
            borderRadius: 10,
            padding: 14,
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div style={{ color: "#9fb0c3", fontSize: 13, marginBottom: 6 }}>
            Operational Interpretation
          </div>

          <div style={{ color: "#d7e7f5", lineHeight: 1.6 }}>
            {tenant.suggested_next_action || "No onboarding interpretation available yet."}
          </div>
        </div>
      </div>

      {/* Operational Event Telemetry */}
      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 6 }}>Operational Event Telemetry</h2>

        <div style={{ color: "#9fb0c3", fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
          Recent operational behavior for this tenant. This is the event window Jay Gomez and Jan Gomez can use
          to understand tenant communication activity and workflow behavior.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 14,
            marginBottom: 16,
          }}
        >
          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Total Events</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{data.oet_summary?.total_events ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Email Events</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{data.oet_summary?.email_events ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Estimate Events</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{data.oet_summary?.by_document_type?.estimate ?? 0}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Invoice Events</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{data.oet_summary?.by_document_type?.invoice ?? 0}</div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          {(data.oet_events || []).length === 0 ? (
            <div style={{ color: "#9fb0c3", background: "#10263a", borderRadius: 10, padding: 14 }}>
              No operational events recorded yet.
            </div>
          ) : (
            (data.oet_events || []).map((event) => (
              <div
                key={event.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 180px 1fr 190px",
                  gap: 12,
                  alignItems: "center",
                  background: "#10263a",
                  borderRadius: 10,
                  padding: 12,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div>
                  <div style={{ fontWeight: 800 }}>{event.activity_type || "—"}</div>
                  <div style={{ color: "#9fb0c3", fontSize: 12 }}>{event.document_type || "—"}</div>
                </div>

                <div style={{ fontWeight: 700 }}>{event.document_number || "—"}</div>

                <div style={{ color: "#d7e7f5", fontSize: 13, lineHeight: 1.5 }}>
                  {event.email_to ? `To: ${event.email_to}` : event.notes || "Operational event recorded."}
                </div>

                <div style={{ color: "#9fb0c3", fontSize: 12 }}>
                  {event.created_at || "—"}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Admin Summary */}
      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Admin Summary</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 14,
            marginBottom: 16,
          }}
        >
          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Workspace Admins</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{adminUsers}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Platform Admins</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{platformAdmins}</div>
          </div>

          <div style={{ background: "#10263a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#9fb0c3", fontSize: 13 }}>Subscription</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {titleCase(tenant.subscription_status)}
            </div>
          </div>
        </div>

        <div style={{ color: "#9fb0c3", fontSize: 14, lineHeight: 1.6 }}>
          This cockpit area is where platform-level controls live for this tenant.
          Later we can add communications, AI, billing, feature flags, and safe
          tenant switching controls here.
        </div>
      </div>

      {/* Users */}
      <div
        id="users-section"
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Users</h2>

        {users.length === 0 ? (
          <p style={{ color: "#9fb0c3" }}>No users found for this tenant.</p>
        ) : (
          <table style={{ width: "100%", marginTop: 12 }}>
            <thead>
              <tr>
                <th align="left">ID</th>
                <th align="left">Email</th>
                <th align="left">Active</th>
                <th align="left">Workspace Admin</th>
                <th align="left">Platform Admin</th>
                <th align="left">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isBusy = actingUserId === u.id;

                return (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>{u.is_active ? "Yes" : "No"}</td>
                    <td>{u.is_admin ? "Yes" : "No"}</td>
                    <td>{u.is_platform_admin ? "Yes" : "No"}</td>


                    <td>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => toggleTenantAdmin(u.id, !u.is_admin)}
                          style={{
                            padding: "6px 10px",
                            background: u.is_admin ? "#3f2a2a" : "#1f4f2a",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: 6,
                            cursor: isBusy ? "not-allowed" : "pointer",
                            opacity: isBusy ? 0.75 : 1,
                          }}
                        >
                          {isBusy
                            ? "Saving..."
                            : u.is_admin
                            ? "Remove Workspace Admin"
                            : "Make Workspace Admin"}
                        </button>


                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() =>
                            togglePlatformAdmin(u.id, !u.is_platform_admin)
                          }
                          style={{
                            padding: "6px 10px",
                            background: u.is_platform_admin ? "#4b1f5e" : "#1e3a5f",
                            color: "#ffffff",
                            border: "none",
                             borderRadius: 6,
                             cursor: isBusy ? "not-allowed" : "pointer",
                             opacity: isBusy ? 0.75 : 1,
                          }}
                        >
                          {isBusy
                            ? "Saving..."
                            : u.is_platform_admin
                            ? "Remove Platform Admin"
                            : "Make Platform Admin"}
                        </button>
                      </div>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Notes */}
      <div
        id="notes-section"
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <h2 style={{ marginTop: 0 }}>Notes</h2>

        <form onSubmit={saveNote} style={{ marginBottom: 18 }}>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add platform note for this tenant..."
            rows={4}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid #27496b",
              background: "#10263a",
              color: "#ffffff",
              padding: 12,
              resize: "vertical",
              outline: "none",
            }}
          />

          <button
            type="submit"
            disabled={savingNote}
            style={{
              marginTop: 12,
              padding: "10px 16px",
              background: "#e0b84f",
              border: "none",
              borderRadius: 8,
              cursor: savingNote ? "not-allowed" : "pointer",
              color: "#0b1b2b",
              fontWeight: 700,
              opacity: savingNote ? 0.75 : 1,
            }}
          >
            {savingNote ? "Saving..." : "Save Note"}
          </button>
        </form>

        {notes.length === 0 ? (
          <div style={{ color: "#9fb0c3" }}>No notes yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {notes.map((note) => (
              <div
                key={note.id}
                style={{
                  background: "#10263a",
                  borderRadius: 10,
                  padding: 14,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>
                    {note.user || "system"}
                  </div>
                  <div style={{ color: "#9fb0c3", fontSize: 13 }}>
                    {fmtDate(note.timestamp)}
                  </div>
                </div>

                <div style={{ whiteSpace: "pre-wrap", color: "#d7e7f5" }}>
                  {note.content}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


